import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Respect reduced-motion preferences by not auto-playing video backgrounds.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Ensure autoplay policies are satisfied before trying to play.
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.playsInline = true;

    const attemptPlay = () => {
      const playPromise = videoElement.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {
          // Browser may still block playback in edge cases.
        });
      }
    };

    videoElement.addEventListener('loadeddata', attemptPlay, { once: true });
    videoElement.load();
    attemptPlay();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
